import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { TenantInput } from '../types';

class TenantService {
  // Get all tenants
  async getTenants() {
    const query = gql`
      query GetTenants {
        tenants {
          id
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for tenants at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL tenants response:', result);
      return result.data.tenants;
    } catch (error: any) {
      console.error('Error fetching tenants:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch tenants: ${error.message}`);
      }
    }
  }

  // Get tenant by ID
  async getTenantById(id: string) {
    const query = gql`
      query GetTenant($id: String!) {
        tenant(id: $id) {
          id
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.tenant;
    } catch (error: any) {
      console.error('Error fetching tenant by ID:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch tenant: ${error.message}`);
      }
    }
  }

  // Create tenant
  async createTenant(input: TenantInput) {
    const mutation = gql`
      mutation CreateTenant($input: TenantInput!) {
        createTenant(input: $input) {
          id
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { input }
      });
      return result.data.createTenant;
    } catch (error: any) {
      console.error('Error creating tenant:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to create tenant: ${error.message}`);
      }
    }
  }

  // Update tenant
  async updateTenant(id: string, input: TenantInput) {
    const mutation = gql`
      mutation UpdateTenant($id: String!, $input: TenantInput!) {
        updateTenant(id: $id, input: $input) {
          id
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, input }
      });
      return result.data.updateTenant;
    } catch (error: any) {
      console.error('Error updating tenant:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to update tenant: ${error.message}`);
      }
    }
  }

  // Delete tenant
  async deleteTenant(id: string) {
    const mutation = gql`
      mutation DeleteTenant($id: String!) {
        deleteTenant(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteTenant;
    } catch (error: any) {
      console.error('Error deleting tenant:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to delete tenant: ${error.message}`);
      }
    }
  }
}

const tenantService = new TenantService();
export default tenantService;
